import type { StorageBridge } from '@casa/bridge';
import type { DriverManifest, DriverPermissions } from '@casa/driver-sdk';
import { loadInstalledDrivers, type LoadedDriver } from './installed_drivers.ts';
import type { DriverCode } from '../messages.ts';

/**
 * Which driver a connection's `driver` names, and with what permissions: a built-in one (with its
 * manifest's permissions) or an installed one (with those the user consented to), if the
 * capability profile of this build allows installed drivers.
 */

/** What a build of the app may do (proposal §6.11). A build switch, with a default per shell. */
export interface CapabilityProfile {
    /** Drivers the user installs. Off on iOS in v1 (App Review), on elsewhere. */
    readonly userDrivers: boolean;
}

/**
 * The profile of this build: `userDriversSwitch` is the build's `VITE_CASA_USER_DRIVERS` (`on` or
 * `off`); without it, the shell decides.
 */
export function capabilityProfile(shell: string | undefined, userDriversSwitch: string | undefined) : CapabilityProfile {
    if (userDriversSwitch === 'on' || userDriversSwitch === 'off') {
        return { userDrivers: userDriversSwitch === 'on' };
    }

    return { userDrivers: shell !== 'ios' };
}

export type ResolvedDriver =
    | { ok: true; code: DriverCode; manifest: DriverManifest; permissions: DriverPermissions; builtIn: boolean }
    | { ok: false; reason: string };

export interface DriverCatalog {
    resolve(name: string) : ResolvedDriver;
    /** The installed drivers, loaded or not, for the drivers page. */
    readonly installed: readonly LoadedDriver[];
    readonly profile: CapabilityProfile;
}

export interface DriverCatalogOptions {
    builtIns: Readonly<Record<string, DriverManifest>>;
    installed: readonly LoadedDriver[];
    profile: CapabilityProfile;
}

export function createDriverCatalog(options: DriverCatalogOptions) : DriverCatalog {
    const installed = new Map(options.installed.map((driver) => [driver.ok ? driver.record.id : driver.id, driver]));

    return {
        resolve(name) {
            const builtIn = Object.hasOwn(options.builtIns, name) ? options.builtIns[name] : undefined;
            if (builtIn) {
                return { ok: true, code: { kind: 'builtIn', name }, manifest: builtIn, permissions: builtIn.permissions, builtIn: true };
            }

            if (!options.profile.userDrivers) {
                return { ok: false, reason: `no built-in driver named "${name}", and this build does not run installed drivers` };
            }

            const loaded = installed.get(name);
            if (!loaded) {
                return { ok: false, reason: `no driver named "${name}" is built in or installed` };
            }
            if (!loaded.ok) {
                return { ok: false, reason: `the installed driver "${name}" does not load: ${loaded.reason}` };
            }

            return { ok: true, code: { kind: 'module', code: loaded.code }, manifest: loaded.manifest, permissions: loaded.record.permissions, builtIn: false };
        },
        installed: options.installed,
        profile: options.profile,
    };
}

/** Loads the installed drivers (unless the profile excludes them) and builds the catalog. */
export async function loadDriverCatalog(storage: StorageBridge, builtIns: Readonly<Record<string, DriverManifest>>, profile: CapabilityProfile) : Promise<DriverCatalog> {
    const installed = profile.userDrivers ? await loadInstalledDrivers(storage) : [];

    return createDriverCatalog({ builtIns, installed, profile });
}
